import { useTranslation } from "react-i18next";
import {
  CountryHeader,
  TripIdeaCard,
  YourTripPillar,
  TailorMadeTravelPillar,
  FairerPillar,
  HowItWorks,
  Pricing,
  Sustainability,
  FAQsList,
  Button,
  Icomoon,
  TravellerStories,
  SimpleCarrousel,
} from "@travellocal/ui";

import { getStaticPageContent } from "components";
import { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import {
  getCountryByCountrySlug,
  getTripsByCountrySlugAndFilters,
  getAgentsByCountryString,
  getPrice,
} from "utils/api/site2";

import { getCountry, getDestinationPage, getDestinationFAQ } from "utils/api/cms";
import { translateRoute } from "utils/localisedRouteHelpers";
import { site1Api, isLocale } from "@travellocal/utils";

type GetStaticPropsOutput = InferGetStaticPropsType<typeof getStaticPropsForCountryV2>;

export type CountryV2Props = GetStaticPropsOutput & { styles: { [key: string]: string } };

export const CountryV2 = ({
  localisedCountryName,
  country,
  matchingTrips: tripIdeas,
  destinationData,
  agents,
  price,
  faq,
  currentLocale,
  styles,
  requestPath,
}: CountryV2Props) => {
  const { t } = useTranslation();
  const ctaUrl = requestPath;

  const com = destinationData.country?.components;

  type Agent = {
    user: { photoURL: string };
    publicName: string;
  };

  const countryHeaderLtos = agents.getAgentsByCountrySlug.map((agent: Agent) => {
    return {
      profileImageUrl: agent.user.photoURL,
      profileImageUrlAlt: agent.publicName,
    };
  });

  const yourTripPillarLto = {
    profileImageUrl: com?.yourtrippillarltoquoteLtoquoteProfileimageurl?.sourceUrl,
    profileImageUrlAlt: com?.yourtrippillarltoquoteLtoquoteProfileimageurl?.altText,
    name: com?.yourtrippillarltoquoteLtoquoteName,
    position: t("web:Destinations_LtoPosition", { destination: localisedCountryName }),
  };

  const tailorMadeLto = {
    profileImageUrl: com?.tailormadetravelpillarltoquoteLtoquoteProfileimageurl?.sourceUrl,
    profileImageUrlAlt: com?.tailormadetravelpillarltoquoteLtoquoteProfileimageurl?.altText,
    name: com?.tailormadetravelpillarltoquoteLtoquoteName,
    position: t("web:Destinations_LtoPosition", { destination: localisedCountryName }),
    quote: '"' + com?.tailormadetravelpillarltoquoteLtoquoteQuote + '"',
  };

  type CountryImage = {
    countryimage: { sourceUrl: string; altText: string };
    countrydaydescription: string;
    countrycityname: string;
  };

  const tailorMadeCountryImages = com?.tailormadetravelpillarItineraryphotos.map(
    (item: CountryImage) => {
      return {
        countryImage: item.countryimage.sourceUrl,
        countryImageAlt: item.countryimage.altText,
        countryCityName: item.countrycityname,
        countryDayDescription: item.countrydaydescription,
      };
    }
  );

  const fairerPillarLto = {
    profileImageUrl: com?.fairerpillarltoquoteLtoquoteProfileimageurl?.sourceUrl,
    profileImageUrlAlt: com?.fairerpillarltoquoteLtoquoteProfileimageurl?.altText,
    name: com?.fairerpillarltoquoteLtoquoteName,
    position: t("web:Destinations_LtoPosition", { destination: localisedCountryName }),
  };

  const destinationsFAQ =
    faq.globalContents?.nodes[0]?.globalContent?.components.faqslistQuestions?.map(
      (item: { question: string; url: string }) => {
        return {
          question: item.question,
          url: item.url,
        };
      }
    );

  const [min, max] = price.getPriceRange.split(" - ");

  type TravellerStory = {
    storyCountryimage: { sourceUrl: string; altText: string };
    storyQuote: string;
    storyTravellername: string;
    storyDescription: string;
  };
  let stories = [];
  if (com?.travellerstoriesStories.length >= 3) {
    stories = com?.travellerstoriesStories?.map((item: TravellerStory) => {
      if (item.storyCountryimage) {
        return {
          countryImage: item.storyCountryimage.sourceUrl,
          countryImageAlt: item.storyCountryimage.altText,
          quote: '"' + item.storyQuote + '"',
          travellerName: item.storyTravellername,
          description: item.storyDescription,
        };
      } else {
        return null;
      }
    });
  }

  const sustainability =
    destinationData.countries.nodes[0].countryTaxonomyContent.countrySustainabilityImage;

  return (
    <>
      <div className={`pb-6 ${styles.countryHeader}`}>
        <CountryHeader
          country={localisedCountryName}
          ltos={countryHeaderLtos}
          countryImage={destinationData.country?.countryheroimage.sourceUrl}
          countryImageAlt={destinationData.country?.countryheroimage.altText}
          scrollToId="trustpilot-section"
        />
      </div>
      <div id="trustpilot-section" className={`container is-fullwidth ${styles.trustpilotSection}`}>
        <HowItWorks
          locale={currentLocale}
          dataTrackingId="destinations-HowItWorks-btn"
          trustpilotProps={{
            locale: currentLocale,
          }}
        />
      </div>
      <div className={`is-fullwidth has-background-sage pb-7 ${styles.yourTripPillar}`}>
        <div className="container">
          <YourTripPillar
            lto={yourTripPillarLto}
            dataTrackingId={"destinations-yourTripPillar"}
            country={localisedCountryName}
            countryImage={com?.yourtrippillarCountryimage?.sourceUrl}
            countryImageAlt={com?.yourtrippillarCountryimage?.altText}
            countryQuote={'"' + com?.yourtrippillarltoquoteLtoquoteQuote + '"'}
            ctaUrl={ctaUrl}
          />
        </div>
      </div>
      <div className={`is-fullwidth has-background-sage ${styles.tailorMadePillar}`}>
        <div className="container">
          <TailorMadeTravelPillar
            lto={tailorMadeLto}
            countryImages={tailorMadeCountryImages}
            ctaUrl={ctaUrl}
            dataTrackingId={"destinations-tailorMade"}
            locale={currentLocale}
            country={localisedCountryName}
          />
        </div>
      </div>
      <div className={`is-fullwidth has-background-sage pt-7 ${styles.fairerPillar}`}>
        <div className="container">
          <FairerPillar
            lto={fairerPillarLto}
            countryImage={com?.fairerpillarCountryimage.sourceUrl}
            countryImageAlt={com?.fairerpillarCountryimage.altText}
            countryQuote={'"' + com?.fairerpillarltoquoteLtoquoteQuote + '"'}
            ctaUrl={ctaUrl}
            dataTrackingId={"destinations-fairer"}
            country={localisedCountryName}
          />
        </div>
      </div>
      {Array.isArray(tripIdeas?.matchingTripsForCurrentPage) &&
        tripIdeas.matchingTripsForCurrentPage.length > 1 && (
          <div className={`${styles.tripIdeas}`}>
            <h2 className="title">
              {t(
                "web:Destinations_Trip_Ideas",
                "The best trips combine the unmissable with the lesser-known"
              )}
            </h2>
            <div className="container mb-5_5 has-text-centered">
              <Button
                element="link"
                isGhost
                className={styles.viewAllTripIdeasButton}
                data-tracking-id={`btn.destination-page.trip-ideas-cta`}
                href={t("routes:tripIdeasByCountry", "/{{country}}/tours/t/trip-ideas", {
                  country: country.slug,
                })}>
                {t("web:Destinations_Trip_Ideas_CTA", "View all trip ideas")}
              </Button>
            </div>
            <div className={styles.rightSideOnViewportEdge}>
              <SimpleCarrousel
                options={{ align: "start", containScroll: "trimSnaps" }}
                emblaClassname={styles.tripIdeaCarousel}>
                {tripIdeas.matchingTripsForCurrentPage.map(
                  ({
                    id,
                    title,
                    mainPhotoURL,
                    amountOfTripDays,
                    tripPath,
                    startingPricePerPerson,
                    currency,
                  }) => {
                    return (
                      <TripIdeaCard
                        key={id}
                        title={title}
                        heroImageUrl={mainPhotoURL}
                        heroImageMeta=""
                        numberOfDays={amountOfTripDays}
                        url={tripPath}
                        pricePerPerson={{
                          price: startingPricePerPerson ?? 0,
                          currency: {
                            availableToCustomers: true,
                            symbol: currency?.symbol,
                            code: currency?.abbreviation,
                          } as site1Api.Currency,
                        }}
                        cardHref={tripPath}
                        cardTarget="_blank"
                        customiseAndRequestHref={tripPath}
                        customiseAndRequestTarget="_blank"
                        customiseAndRequestLabel={t("web:Destinations_Idea_Card", "View trip idea")}
                      />
                    );
                  }
                )}
              </SimpleCarrousel>
            </div>
            <div className="has-text-centered">
              <Button
                className="cta-button"
                colour="teal"
                icomoonIcon={Icomoon.arrowRight3}
                href={ctaUrl}
                data-tracking-id={`destinations-tripIdeas-cta-btn`}>
                {t("web:Destinations_Section_CTA", "Start planning your own")}
              </Button>
            </div>
          </div>
        )}
      {stories.length >= 3 && stories[0] !== null && (
        <div className={`${styles.travellerStories}`}>
          <TravellerStories stories={stories} />
        </div>
      )}
      <div className="container is-fullwidth py-6" style={{ border: `0px solid red` }}>
        <Pricing
          country={localisedCountryName}
          maxPrice={max}
          minPrice={min}
          dataTrackingId="destinations-pricing"
          ctaUrl={ctaUrl}
        />
      </div>
      {sustainability && (
        <div className={`container ${styles.sustainabilitySection}`}>
          <Sustainability
            country={localisedCountryName}
            countryImage={sustainability.sourceUrl}
            countryImageAlt={sustainability.altText}
          />
        </div>
      )}
      {destinationsFAQ && (
        <div className={`container is-fullwidth py-6 ${styles.faqComponent}`}>
          <FAQsList questions={destinationsFAQ} dataTrackingId="destinations-faq" />
          <Button
            isGhost
            data-tracking-id={`faqList-knowledge-base-btn`}
            icomoonIcon={Icomoon.newTab}
            href={
              currentLocale === "en-gb"
                ? "https://support-gb.travellocal.com/"
                : "https://support-en.travellocal.com/"
            }>
            {t("web:Destinations_FAQ_CTA", "TravelLocal knowledge base")}
          </Button>
        </div>
      )}
    </>
  );
};

export const getStaticPropsForCountryV2 = async (context: GetStaticPropsContext) => {
  if (!isLocale(context.locale)) {
    return {
      notFound: true,
    };
  }

  const pageProps = await getStaticPageContent(context);
  const currentLocale =
    pageProps.initialAppState.locale ?? pageProps.initialAppState.detectedLocale;

  const countryParam = Array.isArray(context.params.country)
    ? context.params.country[0].toLowerCase()
    : context.params.country.toLowerCase();
  const dataRequestForm = await getCountry(countryParam, [currentLocale], `get country`);

  if (!dataRequestForm?.nodes?.length) {
    return {
      notFound: true,
    };
  }

  // TODO - temp solution, improve later with api call
  const requestPath = (() => {
    switch (currentLocale) {
      case "de":
        return `/${currentLocale}/${countryParam}/reisen/detaillierte-anfrage/start`;
      default:
        return `/${currentLocale}/${countryParam}/tours/detailed-request-form/start`;
    }
  })();

  const dataTripRequest = await getCountryByCountrySlug(currentLocale, countryParam);

  if (!dataTripRequest) {
    console.error(
      `dataTripRequest is undefined for locale:${currentLocale} and country:${countryParam}. getStaticProps context is ${JSON.stringify(
        context,
        null,
        2
      )}`
    );
  }

  const countriesToTranslate = dataRequestForm?.nodes[0]?.countries.nodes[0]?.countryProfiles.nodes;
  const alternatePages = (countriesToTranslate || []).map((country) => {
    const locale = country.locales.nodes[0].name.toLowerCase();
    return {
      locale,
      url: translateRoute(locale, "destinationsByCountry", {
        country: country.meta.urlFragment,
      }),
    };
  });

  const country = pageProps.countries.find((country) => country.slug === countryParam);

  const initialTripIdeas = await getTripsByCountrySlugAndFilters(
    currentLocale,
    country.slug,
    JSON.stringify({}),
    0,
    8
  );

  const agents = await getAgentsByCountryString(currentLocale, countryParam);
  const price = await getPrice(1, 7, currentLocale, countryParam);

  const faq = await getDestinationFAQ([currentLocale], "getDestinationFAQ");

  const destinationData = await getDestinationPage(
    countryParam,
    [currentLocale],
    "getDestinationPage"
  );

  if (!destinationData) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      pageProps: pageProps,
      currentLocale,
      country: country ?? null,
      alternatePages,
      localisedCountryName: dataTripRequest.name,
      meta: dataRequestForm.nodes[0].meta,
      countryParam,
      destinationData: destinationData,
      matchingTrips: {
        matchingTripsForCurrentPage: initialTripIdeas?.matchingTripsForCurrentPage,
        hasNextPage:
          (initialTripIdeas?.matchingTripsForCurrentPage || []).length > 0 &&
          initialTripIdeas?.hasNextPage,
      },
      agents: agents,
      price: price,
      countryCode: dataTripRequest.code,
      faq: faq,
      requestPath: requestPath,
    },
  };
};
